import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Download, Loader2, BookOpen, Headphones } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PurchaseSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const handleFreeDownload = useCallback(async () => {
    try {
      const downloadToken = sessionId.replace("free_", "");
      const response = await axios.get(`${API}/download/${downloadToken}`);
      setPurchase({
        status: "complete",
        payment_status: "free",
        download_token: downloadToken,
        product_id: response.data.product_id,
        books_included: response.data.books_included,
        includes_audio: response.data.includes_audio
      });
      setLoading(false);
      toast.success("Your free book is ready!");
    } catch (err) {
      console.error("Error getting download info:", err);
      setError("Download link expired or invalid. Please sign up again.");
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = useCallback(async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setError("Payment verification timed out. Please check your email for confirmation.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid" || data.payment_status === "free") {
        setPurchase(data);
        setLoading(false);
        toast.success("Payment successful!");
      } else if (data.status === "expired") {
        setError("Payment session expired. Please try again.");
        setLoading(false);
      } else {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      // Error checking payment
      setError("Error verifying payment. Please contact support.");
      setLoading(false);
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    if (sessionId) {
      if (sessionId.startsWith("free_")) {
        handleFreeDownload();
      } else {
        pollPaymentStatus();
      }
    } else {
      setError("No session ID found");
      setLoading(false);
    }
  }, [sessionId, handleFreeDownload, pollPaymentStatus]);

  const handleDownload = async (bookNumber) => {
    if (!purchase?.download_token) return;
    
    try {
      toast.loading("Preparing download...");
      const response = await axios.get(
        `${API}/download/${purchase.download_token}/book/${bookNumber}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `human_dominion_book_${bookNumber}.epub`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (err) {
      toast.error("Download failed. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-terminal-green mx-auto" />
          <p className="text-[#a3a3a3] font-mono">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 rounded-full bg-signal-red/20 flex items-center justify-center mx-auto">
            <span className="text-signal-red text-2xl">!</span>
          </div>
          <h1 className="text-2xl font-['Syne'] font-bold text-[#e5e5e5]">
            Payment Issue
          </h1>
          <p className="text-[#a3a3a3]">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-signal-red text-white font-mono uppercase tracking-wider hover:bg-red-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        {/* Success Header */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 rounded-full bg-terminal-green/20 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-terminal-green" />
          </div>
          <h1 className="text-3xl font-['Syne'] font-bold text-[#e5e5e5]">
            Thank You!
          </h1>
          <p className="text-[#a3a3a3]">
            Your purchase is complete. Download your books below.
          </p>
        </div>

        {/* Download Section */}
        <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
          <h2 className="font-['Syne'] font-bold text-lg text-[#e5e5e5]">
            Your Downloads
          </h2>
          
          <div className="space-y-3">
            {purchase?.books_included?.map((bookNum) => (
              <div 
                key={bookNum}
                className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-[#262626]"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-signal-red" />
                  <div>
                    <div className="font-mono text-sm text-[#e5e5e5]">
                      Book {bookNum}
                    </div>
                    <div className="text-xs text-[#525252]">
                      EPUB format (Kindle compatible)
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(bookNum)}
                  className="px-4 py-2 bg-terminal-green text-[#0a0a0a] font-mono text-sm font-bold hover:bg-green-400 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            ))}
            
            {purchase?.includes_audio && (
              <div className="p-4 bg-[#0a0a0a] border border-signal-red">
                <div className="flex items-center gap-3">
                  <Headphones className="w-5 h-5 text-signal-red" />
                  <div>
                    <div className="font-mono text-sm text-[#e5e5e5]">
                      Audiobook Access
                    </div>
                    <div className="text-xs text-[#a3a3a3]">
                      Generate narration in the reader app
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Download Token */}
        <div className="bg-[#141414] border border-[#262626] p-4 text-center">
          <p className="text-xs text-[#525252] mb-2">Your download token (save this):</p>
          <code className="text-terminal-green font-mono text-sm bg-[#0a0a0a] px-3 py-1">
            {purchase?.download_token}
          </code>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/app")}
            className="px-6 py-3 bg-signal-red text-white font-mono uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <BookOpen className="w-5 h-5" />
            Start Reading
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 border border-[#262626] text-[#a3a3a3] font-mono uppercase tracking-wider hover:border-[#e5e5e5] hover:text-[#e5e5e5] transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    </div>
  );
}
